/**
 * Storage utilities for handling localStorage quota issues
 */

// Check localStorage quota and data size
export const checkLocalStorageQuota = (key, data) => {
  try {
    const jsonString = JSON.stringify(data);
    const sizeInBytes = new Blob([jsonString]).size;
    const sizeInMB = sizeInBytes / (1024 * 1024);
    
    console.log(`📊 Data size for ${key}: ${sizeInMB.toFixed(2)} MB`);
    
    // Check if data is too large (> 4MB to be safe)
    if (sizeInMB > 4) {
      console.warn(`⚠️ Data too large for localStorage: ${sizeInMB.toFixed(2)} MB`);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error checking data size:", error);
    return false;
  }
};

// Safe localStorage setItem with quota handling
export const safeLocalStorageSetItem = (key, data, onQuotaExceeded = null) => {
  try {
    // Check size first
    if (!checkLocalStorageQuota(key, data)) {
      console.warn(`⚠️ Data too large for localStorage: ${key}`);
      localStorage.removeItem(key); // Clear any existing data
      if (onQuotaExceeded) {
        onQuotaExceeded('Data too large (>4MB)');
      }
      return false;
    }

    // Try to save
    localStorage.setItem(key, JSON.stringify(data));
    console.log(`✅ Successfully saved ${key} to localStorage`);
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError' || error.code === 22) {
      console.warn(`⚠️ localStorage quota exceeded for ${key}`);
      localStorage.removeItem(key); // Clear any existing data
      if (onQuotaExceeded) {
        onQuotaExceeded('localStorage quota exceeded');
      }
      return false;
    } else {
      console.error(`❌ Error saving ${key} to localStorage:`, error);
      throw error;
    }
  }
};

// Get localStorage usage info
export const getLocalStorageUsage = () => {
  let totalSize = 0;
  const usage = {};
  
  for (let key in localStorage) {
    if (localStorage.hasOwnProperty(key)) {
      const value = localStorage.getItem(key);
      const size = new Blob([value]).size;
      usage[key] = {
        size: size,
        sizeInMB: (size / (1024 * 1024)).toFixed(2)
      };
      totalSize += size;
    }
  }
  
  return {
    totalSize: totalSize,
    totalSizeInMB: (totalSize / (1024 * 1024)).toFixed(2),
    items: usage,
    availableSpace: (5 * 1024 * 1024) - totalSize, // Assuming 5MB limit
    availableSpaceInMB: ((5 * 1024 * 1024) - totalSize) / (1024 * 1024)
  };
};

// Clean up localStorage by removing large items
export const cleanupLocalStorage = (maxSizeInMB = 4) => {
  const usage = getLocalStorageUsage();
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  
  console.log('🧹 Starting localStorage cleanup...');
  console.log(`Current usage: ${usage.totalSizeInMB} MB`);
  
  // Sort items by size (largest first)
  const sortedItems = Object.entries(usage.items)
    .sort(([,a], [,b]) => b.size - a.size);
  
  let removedItems = [];
  let currentSize = usage.totalSize;
  
  for (const [key, info] of sortedItems) {
    if (currentSize <= maxSizeInBytes) break;
    
    // Don't remove critical items
    const criticalKeys = ['admin_token', 'admin_user', 'test_passwords'];
    if (criticalKeys.includes(key)) continue;
    
    localStorage.removeItem(key);
    removedItems.push({ key, size: info.sizeInMB });
    currentSize -= info.size;
    
    console.log(`🗑️ Removed ${key} (${info.sizeInMB} MB)`);
  }
  
  const newUsage = getLocalStorageUsage();
  console.log(`✅ Cleanup complete. New usage: ${newUsage.totalSizeInMB} MB`);
  
  return {
    removedItems,
    oldUsage: usage.totalSizeInMB,
    newUsage: newUsage.totalSizeInMB,
    spaceSaved: (usage.totalSize - newUsage.totalSize) / (1024 * 1024)
  };
};

// Compress image data for storage
export const compressImageForStorage = (file, maxWidth = 800, quality = 0.8) => {
  return new Promise((resolve) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = function() {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > maxWidth || height > maxWidth) {
        if (width > height) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        } else {
          width = (width * maxWidth) / height;
          height = maxWidth;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob(function(compressedBlob) {
        const reader = new FileReader();
        reader.onload = function() {
          resolve(reader.result);
        };
        reader.readAsDataURL(compressedBlob);
      }, 'image/jpeg', quality);
    };
    
    img.src = URL.createObjectURL(file);
  });
};

export default {
  checkLocalStorageQuota,
  safeLocalStorageSetItem,
  getLocalStorageUsage,
  cleanupLocalStorage,
  compressImageForStorage
};
